import JSZip from 'jszip';

// Exibicao amigavel de tamanhos em MB (1 casa decimal) nas mensagens de
// popup - os valores crus continuam em bytes internamente.
export function formatMegabytes(bytes) {
    return (bytes / (1024 * 1024)).toFixed(1);
}

// FR-030 (Tarefa 5.7): mesmo limite de nome ja aplicado ao skill-creator
// (backend/skills/helpers.py::MAX_NAME_CHARS, env SKILL_MAX_NAME_CHARS)
// tambem vale pro nome extraido do frontmatter de um arquivo importado.
// `[REVISADO na Tarefa 11.1, FR-030]`: valor oficial subiu de 60 para 64.
export const SKILL_MAX_NAME_CHARS = Number(
    process.env.REACT_APP_SKILL_MAX_NAME_CHARS || 64,
);

// Tarefa 11.1 (FR-077): novo limite de descricao, mesmo padrao de
// SKILL_MAX_NAME_CHARS (backend/skills/helpers.py::MAX_DESCRIPTION_CHARS,
// env SKILL_MAX_DESCRIPTION_CHARS).
export const SKILL_MAX_DESCRIPTION_CHARS = Number(
    process.env.REACT_APP_SKILL_MAX_DESCRIPTION_CHARS || 1024,
);

const FRONTMATTER_RE = /^---\s*\n([\s\S]*?)\n---\s*\n/;

// Extracao minima de um campo do frontmatter YAML (linha unica "campo: valor")
// - so o suficiente para o limite de caracteres do nome antes do upload
// (FR-030) e para previews otimistas no cliente; o parsing completo e a
// validacao de verdade continuam no backend (parse_skill_md, yaml.safe_load).
function extractFrontmatterField(text, field) {
    const match = FRONTMATTER_RE.exec(text);
    if (!match) return null;
    const fieldRe = new RegExp(`^${field}\\s*:`);
    const line = match[1].split('\n').find((l) => fieldRe.test(l));
    if (!line) return null;
    return line.slice(line.indexOf(':') + 1).trim();
}

// Contraparte de backend/skills/helpers.py::build_skill_md_frontmatter - monta
// o cabecalho `---\n...\n---` do SKILL.md sempre como YAML valido, aspando e
// escapando name/description (bugfix: um template literal cru quebrava o
// parser YAML quando a descricao tinha ':' fora de aspas, ou perdia parte do
// texto em silencio quando tinha quebra de linha com ':' na linha seguinte -
// yaml.safe_load interpretava como uma segunda chave solta).
function yamlQuoteScalar(value) {
    return (
        '"' +
        String(value)
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\"')
            .replace(/\n/g, '\\n')
            .replace(/\t/g, '\\t') +
        '"'
    );
}

export function buildSkillMdFrontmatter(name, description) {
    return `---\nname: ${yamlQuoteScalar(name)}\ndescription: ${yamlQuoteScalar(description)}\n---\n`;
}

function extractNameFromFrontmatter(text) {
    return extractFrontmatterField(text, 'name');
}

function extractDescriptionFromFrontmatter(text) {
    return extractFrontmatterField(text, 'description');
}

async function findSkillMdTextInZip(file) {
    const zip = await JSZip.loadAsync(file);
    const entry = Object.values(zip.files).find(
        (e) => !e.dir && e.name.split('/').pop().toUpperCase() === 'SKILL.MD',
    );
    return entry ? entry.async('string') : null;
}

// Devolve o nome declarado no frontmatter do arquivo de skill (.md ou .zip
// com um SKILL.md dentro), ou null se nao for possivel extrair - nesse caso a
// validacao de tamanho/formato (getSkillFileSizes) ou o proprio backend
// (create_skill) reportam o problema real, sem bloquear aqui por um nome
// que nao foi encontrado.
export async function extractSkillFileName(file) {
    const lowerName = (file.name || '').toLowerCase();
    try {
        if (lowerName.endsWith('.zip') || lowerName.endsWith('.skill')) {
            const text = await findSkillMdTextInZip(file);
            return text ? extractNameFromFrontmatter(text) : null;
        }
        if (lowerName.endsWith('.md')) {
            return extractNameFromFrontmatter(await file.text());
        }
    } catch {
        return null;
    }
    return null;
}

// Tarefa 11.1 (FR-077): mesmo raciocinio de extractSkillFileName, mas pra
// descricao - usado pra bloquear o upload ANTES do envio quando a descricao
// do frontmatter excede SKILL_MAX_DESCRIPTION_CHARS.
export async function extractSkillFileDescription(file) {
    const lowerName = (file.name || '').toLowerCase();
    try {
        if (lowerName.endsWith('.zip') || lowerName.endsWith('.skill')) {
            const text = await findSkillMdTextInZip(file);
            return text ? extractDescriptionFromFrontmatter(text) : null;
        }
        if (lowerName.endsWith('.md')) {
            return extractDescriptionFromFrontmatter(await file.text());
        }
    } catch {
        return null;
    }
    return null;
}

// Preview otimista de um arquivo de skill (.md ou .zip/.skill) direto no
// navegador, sem round-trip ao backend - usado por "Substituir skill"
// (SkillDetailView.js) pra mostrar o resultado aparente (conteudo, nome,
// descricao, arvore de arquivos) imediatamente ao confirmar, reconciliado
// com a resposta real do PUT /put_skill assim que ela chega (ou desfeito, se
// o backend recusar). `null` quando nao e' possivel montar um preview (ex.:
// zip sem SKILL.md) - quem chama trata como "sem preview", deixando a
// validacao real (mesma de sempre) reportar o problema.
export async function extractSkillPreview(file) {
    const lowerName = (file.name || '').toLowerCase();
    try {
        if (lowerName.endsWith('.zip') || lowerName.endsWith('.skill')) {
            const zip = await JSZip.loadAsync(file);
            const entries = Object.values(zip.files).filter((entry) => !entry.dir);
            const skillMdEntry = entries.find(
                (entry) => entry.name.split('/').pop().toUpperCase() === 'SKILL.MD',
            );
            if (!skillMdEntry) return null;
            const content = await skillMdEntry.async('string');
            return {
                content,
                files: entries.map((entry) => ({ path: entry.name })),
                name: extractFrontmatterField(content, 'name'),
                description: extractFrontmatterField(content, 'description'),
            };
        }
        if (lowerName.endsWith('.md')) {
            const content = await file.text();
            return {
                content,
                files: [{ path: 'SKILL.md' }],
                name: extractFrontmatterField(content, 'name'),
                description: extractFrontmatterField(content, 'description'),
            };
        }
    } catch {
        return null;
    }
    return null;
}

async function zipEntrySizes(file) {
    const zip = await JSZip.loadAsync(file);
    const entries = Object.values(zip.files).filter((entry) => !entry.dir);
    const sizes = [];
    for (const entry of entries) {
        const bytes = await entry.async('uint8array');
        sizes.push({ path: entry.name, bytes: bytes.length });
    }
    return sizes;
}

export class UnsupportedSkillFileError extends Error {}

// Tamanho (em bytes) de cada arquivo dentro de um arquivo de skill (.md ou
// .zip) antes de qualquer envio ao backend (FR-013/FR-052) - para .md, um
// unico arquivo (o proprio, via File.size); para .zip, um item por entrada,
// espelhando o manifesto que o backend vai validar (_validate_files).
export async function getSkillFileSizes(file) {
    const lowerName = (file.name || '').toLowerCase();
    // '.skill' e' o formato empacotado da spec Agent Skills (zip renomeado,
    // suporte adicionado no merge com dev em backend/skills/views.py e
    // extractSkillFileName acima) - mesmo tratamento de '.zip' aqui tambem,
    // senao a checagem de tamanho rejeitaria como "formato nao suportado"
    // um arquivo que o backend aceita normalmente.
    if (lowerName.endsWith('.zip') || lowerName.endsWith('.skill')) return zipEntrySizes(file);
    if (lowerName.endsWith('.md')) return [{ path: file.name, bytes: file.size }];
    throw new UnsupportedSkillFileError(`formato nao suportado: ${file.name}`);
}

// Reaproveitada pela deteccao de "tipo de arquivo" do painel de conteudo da
// tela "Detalhe da Skill" (Tarefa 8.2, CodeHighlight.js/SkillDetailView.js).
// `[REVISADO na Tarefa 11.3, FR-075]`: tambem servia a allow-list de
// extensoes aceitas em upload (FR-054, `ALLOWED_FILE_EXTENSIONS`/
// `findRejectedExtensions`) - removida; qualquer extensao e' aceita agora.
export function extensionOf(path) {
    const base = path.split('/').pop() || '';
    return base.includes('.') ? base.slice(base.lastIndexOf('.') + 1).toLowerCase() : '';
}

// Bugfix pos-Tarefa 7.7: abrir um arquivo de texto grande travava a aba -
// tanto "Previa" (react-markdown/remark-gfm monta uma arvore de milhares de
// nos) quanto "Codigo" (um <pre> so tambem fica pesado nessa escala) tentavam
// renderizar o conteudo inteiro de uma vez. Acima deste limite (so' de
// EXIBICAO - nao afeta upload/armazenamento, intocado pela remocao dos
// limites de upload na Tarefa 11.2/FR-074), nem "Previa" nem "Codigo" tentam
// renderizar; a tela mostra uma mensagem + botao de download (decisao tomada
// com o usuario em 2026-08-13).
export const CONTENT_PREVIEW_MAX_BYTES = Number(
    process.env.REACT_APP_CONTENT_PREVIEW_MAX_BYTES || 1024 * 1024,
);

export function textByteLength(text) {
    return new TextEncoder().encode(text || '').length;
}

// Download client-side (sem round-trip ao backend) - o conteudo do arquivo
// grande demais para pre-visualizar ja esta em memoria (foi ele quem
// disparou o problema), entao so' precisa virar um Blob.
export function downloadTextAsFile(text, filename) {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
